import { UseFormReturn } from 'react-hook-form';
import { CreateAppUserPayload, CreateAdminUserPayload } from '../../../api/admin';

interface AppUserFormProps {
    form: UseFormReturn<CreateAppUserPayload>;
    onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    isLoading: boolean;
    onCancel: () => void;
}

export function AppUserForm({ form, onSubmit, isLoading, onCancel }: AppUserFormProps) {
    const { register, formState: { errors } } = form;
    return (
        <form className="space-y-4" onSubmit={onSubmit}>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">หมายเลขโทรศัพท์ *</label>
                <input
                    type="tel"
                    {...register('phoneNumber', { required: 'กรุณากรอกหมายเลขโทรศัพท์' })}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
                />
                {errors.phoneNumber && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.phoneNumber.message as string}</p>
                )}
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">ชื่อ</label>
                    <input
                        type="text"
                        {...register('firstName')}
                        className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">นามสกุล</label>
                    <input
                        type="text"
                        {...register('lastName')}
                        className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
                    />
                </div>
            </div>
            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    ยกเลิก
                </button>
                <button
                    type="submit"
                    className="rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-600"
                    disabled={isLoading}
                >
                    {isLoading ? 'กำลังบันทึก...' : 'บันทึกผู้ใช้'}
                </button>
            </div>
        </form>
    );
}

interface AdminUserFormProps {
    form: UseFormReturn<CreateAdminUserPayload>;
    onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    isLoading: boolean;
    onCancel: () => void;
}

export function AdminUserForm({ form, onSubmit, isLoading, onCancel }: AdminUserFormProps) {
    const { register, formState: { errors } } = form;
    return (
        <form className="space-y-4" onSubmit={onSubmit}>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">ชื่อผู้ใช้ *</label>
                <input
                    type="text"
                    {...register('username', {
                        required: 'กรุณากรอกชื่อผู้ใช้',
                        minLength: { value: 3, message: 'ชื่อผู้ใช้ต้องมีอย่างน้อย 3 ตัวอักษร' },
                    })}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
                />
                {errors.username && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.username.message as string}</p>
                )}
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">รหัสผ่าน *</label>
                <input
                    type="password"
                    {...register('password', {
                        required: 'กรุณากรอกรหัสผ่าน',
                        minLength: { value: 8, message: 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร' },
                        pattern: {
                            value: /^(?=.*[A-Za-z])(?=.*\d).{8,}$/,
                            message: 'รหัสผ่านต้องมีทั้งตัวอักษรและตัวเลข',
                        },
                    })}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
                    placeholder="กำหนดรหัสผ่านสำหรับพนักงาน"
                />
                {errors.password && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.password.message as string}</p>
                )}
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    ต้องประกอบด้วยตัวอักษรและตัวเลขอย่างน้อย 8 ตัวอักษร
                </p>
            </div>
            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    ยกเลิก
                </button>
                <button
                    type="submit"
                    className="rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-600"
                    disabled={isLoading}
                >
                    {isLoading ? 'กำลังบันทึก...' : 'บันทึกผู้ใช้'}
                </button>
            </div>
        </form>
    );
}

interface EditAdminFormProps {
    form: UseFormReturn<{ username: string; password: string; status: 'active' | 'inactive' }>;
    onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    isLoading: boolean;
    onCancel: () => void;
    isDefaultUser: boolean;
}

export function EditAdminForm({ form, onSubmit, isLoading, onCancel, isDefaultUser }: EditAdminFormProps) {
    const { register, formState: { errors } } = form;
    return (
        <form className="px-6 py-5 space-y-4" onSubmit={onSubmit}>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">ชื่อผู้ใช้ *</label>
                <input
                    type="text"
                    {...register('username', {
                        required: 'กรุณากรอกชื่อผู้ใช้',
                        minLength: { value: 3, message: 'ชื่อผู้ใช้ต้องมีอย่างน้อย 3 ตัวอักษร' },
                    })}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
                />
                {errors.username && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.username.message}</p>
                )}
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">รหัสผ่านใหม่ (ไม่บังคับ)</label>
                <input
                    type="password"
                    {...register('password', {
                        validate: (value: string) => {
                            if (!value) return true;
                            if (value.length < 8) {
                                return 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร';
                            }
                            if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(value)) {
                                return 'รหัสผ่านต้องมีทั้งตัวอักษรและตัวเลข';
                            }
                            return true;
                        },
                    })}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500"
                    placeholder="เว้นว่างหากไม่ต้องการเปลี่ยนรหัสผ่าน"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">ต้องประกอบด้วยตัวอักษรและตัวเลขอย่างน้อย 8 ตัวอักษร</p>
                {errors.password && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.password.message}</p>
                )}
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">สถานะการใช้งาน</label>
                <select
                    {...register('status')}
                    disabled={isDefaultUser}
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-orange-500 disabled:bg-gray-100 dark:disabled:bg-gray-700"
                >
                    <option value="active">ใช้งานอยู่</option>
                    <option value="inactive">ปิดใช้งาน</option>
                </select>
                {isDefaultUser && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">ไม่สามารถปิดใช้งานบัญชีเริ่มต้นได้</p>
                )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    ยกเลิก
                </button>
                <button
                    type="submit"
                    className="rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-600 disabled:opacity-50"
                    disabled={isLoading}
                >
                    {isLoading ? 'กำลังบันทึก...' : 'บันทึกการเปลี่ยนแปลง'}
                </button>
            </div>
        </form>
    );
}
